/**
 * The Choreographer — every card animation in the game, in one place.
 *
 * ## Why this file matters
 *
 * The Three.js sibling gets weight for free: a card that rises really is
 * closer to the camera, and its shadow really does separate. Here there is no
 * camera and no depth buffer, so weight has to be *manufactured* — out of
 * timing, scale, rotation, skew, and above all out of `lift`.
 *
 * `lift` is the load-bearing idea. It is a card's virtual height above the
 * felt, in the same units the shadow model in `layout.ts` uses. The card layer
 * derives its drop shadow (offset direction from `LIGHT`, length from lift,
 * blur and alpha from lift) and its specular parallax from that one number.
 * This module owns *when* lift changes; the card layer owns what it looks
 * like. Raise it during flight and the shadow slides out from under the card
 * and goes soft — which is the single frame that tells the player the card
 * left the table. Everything else here is in service of that.
 *
 * ## Ownership split with the card layer
 *
 *   this module writes → x, y, rotation, scale, skew, alpha, visible, zIndex,
 *                        lift, flip, glow
 *   the card layer reads → lift  (shadow offset/blur/alpha, specular parallax)
 *                          flip  (0..1 turn progress; face shown at >= 0.5)
 *                          glow  (0..1 emissive rim, for wilds and payoffs)
 *
 * The card layer must NOT also scale or offset the sprite from `lift` — that
 * would double up with the scale this module already animates. Lift drives the
 * *shadow and the shading*, nothing else.
 *
 * ## House rules, enforced here
 *
 * - Every animation is interruptible. Starting a new animation on a card
 *   instantly snaps the old one to its finished state; nothing queues.
 * - Fast play compresses rather than backlogs: concurrent animations raise the
 *   global timeScale, and past a hard cap the oldest animation is finished
 *   outright. `fastForward()` cuts everything to its end state at once.
 * - Nothing the player waits on exceeds `CRITICAL_PATH_MAX` (600ms). Awaitable
 *   methods resolve at the moment the move is legible; the decorative tail
 *   (the elastic settle, the sweep to the discard) keeps playing after.
 * - Reduced motion is handled by the `dur`/`amp`/`ease` filters in
 *   `./easing` — positions still animate, durations halve, flourish and shake
 *   go to zero.
 */

import type { Container } from 'pixi.js';
import { gsap } from 'gsap';
import { isWildCard } from '@/game/rules';
import type { CardId } from '@/game/types';
import {
  CARD,
  DISCARD,
  PILE_JITTER,
  DESIGN,
  STOCK,
  pileCardPosition,
  pilePosition,
} from '@/pixi/layout';
import {
  CRITICAL_PATH_MAX,
  DUR,
  EASE,
  LIFT,
  POP,
  SHAKE_FREQ,
  SPAN,
  TICK,
  TILT,
  amp,
  clamp,
  clamp01,
  dur,
  ease,
  jitter,
  lerp,
  prefersReducedMotion,
  setReducedMotion,
} from '@/motion/easing';

/* ------------------------------------------------------------------ *
 * The surface this module needs from a card
 * ------------------------------------------------------------------ */

/**
 * The animatable surface of a card. `CardSprite` from `@/pixi/card` satisfies
 * this structurally — it is a `Container` plus the three depth channels.
 */
export interface CardView extends Container {
  /** Which card this sprite is currently showing, e.g. `'as'`, `'jc'`. */
  readonly cardId: CardId;
  /**
   * Virtual height above the felt, in the units `SHADOW` in `layout.ts` uses.
   * `0` is flat on the table. The card layer derives shadow offset, blur and
   * alpha — and the specular parallax — from this.
   */
  lift: number;
  /**
   * Turn progress. `0` = back showing, `1` = face showing. The face swaps at
   * `>= 0.5`; intermediate values exist so the shader can swing the specular
   * through the turn. The edge-on squash is animated here as `scale.x`, so the
   * card layer must not squash from `flip` as well.
   */
  flip: number;
  /** Emissive rim, `0..1`. Wild reveals, 21 snaps, five-card cascades. */
  glow: number;
}

/** How the Choreographer gets hold of sprites. `CardPool` satisfies this. */
export interface CardLookup {
  /** The live sprite for a card id, checking one out of the pool if needed. */
  get(id: CardId): CardView | undefined;
  /** Optional: hand a sprite back once it has left the table. */
  recycle?(id: CardId): void;
}

/** The four ways a pile can end. Mirrors `BonusKind` plus the unhappy one. */
export type ResolveKind = 'twentyOne' | 'fiveCard' | 'wild' | 'bust';

/** Pile-well feedback states. */
export type NudgeKind = 'hover' | 'hoverOut' | 'press' | 'reject';

export interface ChoreographerOptions {
  /** Where card sprites come from. */
  cards: CardLookup;
  /**
   * The container screen shake is applied to — normally the world root, above
   * the letterbox transform. Whatever is passed, nothing else may animate its
   * `x`, `y` or `rotation`: shake owns them and restores them exactly.
   */
  shakeTarget?: Container;
  /** Per-pile well containers, indexed by pile. Used for hover/press feedback. */
  pileWells?: readonly Container[];
  /**
   * Fire the impact shake from inside `resolvePile` (default `true`). Turn it
   * off if the VFX layer would rather drive shake off the bus itself —
   * otherwise a bust shakes twice.
   */
  autoShake?: boolean;
  /** Force reduced motion on/off. Omit to follow `prefers-reduced-motion`. */
  reducedMotion?: boolean;
}

/* ------------------------------------------------------------------ *
 * Layering
 * ------------------------------------------------------------------ */

/**
 * zIndex bands. Exported so the table, totals and VFX layers can slot in
 * without guessing. Requires `sortableChildren` on the card container.
 */
export const Z = {
  well: 10,
  /** Base for cards resting in piles. */
  pile: 100,
  /** Spacing between piles so pile 3's bottom card still sits over pile 2's top. */
  pileStride: 20,
  /** The face-up stock. */
  stock: 600,
  /** A card in the air. Always over every resting card. */
  flight: 900,
  /** A resolving pile, held up for the player to read. */
  hero: 1200,
} as const;

/**
 * How many stock cards the opening deal actually animates.
 *
 * Twelve rather than eight: the opening riffles the deck, and a riffle needs
 * two packets that read as packets. Four cards a side is a gesture; six is a
 * shuffle. Everything downstream — `main.ts`'s restack window, its pool
 * prewarm, the `#stockSlot` z band — is written in terms of this constant, so
 * it is safe to move.
 */
export const DEAL_VISIBLE = 12;

/** Vertical step between stacked stock cards, so the deck reads as thick. */
const STOCK_STEP = PILE_JITTER * 0.45;

/**
 * Where the deck comes on from — off table, and it has to be off the *nearest*
 * edge to wherever the stock actually is.
 *
 * A function rather than a constant because `STOCK` moves: it is on the left in
 * landscape and in the middle of the grid in portrait, and a value baked at
 * import would keep dealing to the old place after a rotation.
 *
 * Landscape keeps the original: in from the left, level with the stock and a
 * touch low, so the block slides onto the table rather than dropping onto it —
 * the short way in, and the direction that reads as a dealer's hand. Portrait
 * cannot use it. With the stock in the centre of the grid, a card entering from
 * the side flies straight across a whole column of piles, and the deck is
 * replenished after every card played, so that is not a one-off — it is the
 * animation the player sees most. Coming up from under the table instead keeps
 * the path clear of every pile and still reads as a deck being drawn from.
 */
function dealOrigin(): { x: number; y: number } {
  if (STOCK.x < DESIGN.width * 0.35) {
    return { x: -CARD.width * 0.85, y: STOCK.y + CARD.height * 0.14 };
  }
  return { x: STOCK.x, y: DESIGN.height + CARD.height * 0.85 };
}

/**
 * Which half of the riffle a card belongs to, by its depth in the deck.
 *
 * Alternating by depth rather than splitting the deck into two contiguous
 * blocks is what lets the cascade run left, right, left, right while the deck
 * order stays exactly as the engine dealt it — and alternating packets are
 * what a riffle produces anyway. From above the two states are
 * indistinguishable during the split; only the interleave shows the
 * difference, and the interleave is the part worth watching.
 */
const riffleSide = (depth: number): number => (depth % 2 === 0 ? -1 : 1);

/** Each concurrent animation speeds everything up by this much. */
const PRESSURE_GAIN = 0.35;
/** Ceiling on that compression — beyond this it stops reading as animation. */
const MAX_TIME_SCALE = 2.4;
/** Past this many live animations the oldest is cut, never queued. */
const MAX_CONCURRENT = 6;
/** Resting glow the wild keeps while it is the active card. */
/**
 * How lit a wild sits at rest.
 *
 * Down from 0.45. The foil's rim was doing two jobs at once — saying "this card
 * is special" continuously, and marking the moment one turns up — and the first
 * was loud enough to eat the second. A calmer rest is what gives the arrival
 * flare something to be louder than.
 */
const WILD_IDLE_GLOW = 0.32;

/* ------------------------------------------------------------------ *
 * Job bookkeeping
 * ------------------------------------------------------------------ */

interface Job {
  readonly tl: GSAPTimeline;
  readonly targets: readonly object[];
  /** Writes the exact end state. Must never start another job. */
  readonly finalize: () => void;
  readonly resolve: () => void;
  resolved: boolean;
  finished: boolean;
  /**
   * Exempt from `#retune`. The opening deal pins its own scale deliberately —
   * see the note where it does — and must not be re-timed by later pressure.
   */
  pinned: boolean;
}

/* ------------------------------------------------------------------ *
 * Choreographer
 * ------------------------------------------------------------------ */

export class Choreographer {
  readonly #cards: CardLookup;
  readonly #live = new Set<Job>();
  readonly #owner = new Map<object, Job>();

  #wells: readonly Container[];
  #shakeTarget: Container | null;
  #shakeBase: { x: number; y: number; rotation: number } | null = null;
  #shakeSeed = 0;
  #autoShake: boolean;
  #speed = 1;
  /**
   * Hit-stop: a brief, deliberate slowing of everything on the table.
   *
   * The single cheapest upgrade to impact there is, and there was none anywhere
   * in this build. Amplitude alone (a shake, a flash) says *something loud
   * happened*; taking time away says *that had mass*. Two frames is below
   * conscious perception and reads purely as weight.
   */
  #hitStopFactor = 1;
  #hitStopTimer: ReturnType<typeof setTimeout> | null = null;
  #onTimeScale: ((factor: number) => void) | undefined;
  #hovered: number | null = null;

  constructor(options: ChoreographerOptions) {
    this.#cards = options.cards;
    this.#wells = options.pileWells ?? [];
    this.#shakeTarget = options.shakeTarget ?? null;
    this.#autoShake = options.autoShake ?? true;
    if (options.reducedMotion !== undefined) setReducedMotion(options.reducedMotion);
  }

  /* -------------------------------------------------- *
   * Wiring
   * -------------------------------------------------- */

  /** Late-bind the pile wells, e.g. once the table has been built. */
  setPileWells(wells: readonly Container[]): void {
    this.#wells = wells;
  }

  /** Late-bind the screen-shake target. A shake in flight is cut and undone. */
  setShakeTarget(target: Container | null): void {
    if (this.#shakeTarget) this.#interrupt(this.#shakeTarget);
    this.#restoreShake();
    this.#shakeTarget = target;
  }

  /** Global speed multiplier. `1` is authored speed; higher is faster. */
  get speed(): number {
    return this.#speed;
  }

  set speed(value: number) {
    this.#speed = clamp(value, 0.25, 4);
  }

  /** True while anything is still moving. */
  get isBusy(): boolean {
    return this.#live.size > 0;
  }

  /**
   * Cut everything to its finished state, now. Positions, rotations, lifts and
   * visibility all land exactly where they would have; every pending promise
   * resolves. Safe to call at any time — on a fast click, on a resize, on
   * game over.
   */
  fastForward(): void {
    for (const job of [...this.#live]) this.#finish(job);
  }

  /** Kill everything and let go of the wiring. */
  dispose(): void {
    this.fastForward();
    this.#restoreShake();
    this.#live.clear();
    this.#owner.clear();
    this.#wells = [];
    this.#shakeTarget = null;
  }

  /* -------------------------------------------------- *
   * The opening
   * -------------------------------------------------- */

  /**
   * The opening: a dealer putting a deck on the table. Three beats, in the
   * order hands actually work.
   *
   * 1. **Sweep.** The visible top of the deck arrives from off the left edge as
   *    one tight block — every card keeps its offset within the stack while it
   *    travels, so it reads as a single object — with a faint ripple running
   *    through it so it also reads as many. It lands *loose*: knocked out of
   *    true, the way a deck dropped on a table lands, never machine-stacked.
   *
   * 2. **Riffle.** The one beat 2D can do better than the 3D sibling. The deck
   *    fans, the two halves part sideways and ride up on `lift` until their
   *    shadows come out from under them and go soft — on this branch that
   *    shadow separation *is* the depth cue — then the cards zip back together
   *    in an alternating cascade, left, right, left, right, a couple of frames
   *    apart. Snap eases throughout, never springs: a riffle is mechanical.
   *
   * 3. **Square.** The dealer squares the deck. Two taps — sides, then ends —
   *    each a small overshoot onto the true slot with a press into the felt.
   *    This is the beat that makes the opening read as hands rather than
   *    tweens, and it is the one the old deal had nothing of.
   *
   * Only the top `DEAL_VISIBLE` cards are animated; the rest of the 52 are
   * implied by the stack, because a 52-card intro is a 52-card wait and the
   * round is only 180 seconds long.
   *
   * **Why two jobs.** The whole opening runs ~0.9s, but the gate opens the
   * instant the last card is down (~0.65s) — well before the squaring finishes.
   * So the top card, the one the player is about to flip, gets a job of its own
   * and owns the gate, while the rest of the deck owns the squaring.
   * `flipStockCard` interrupts only the card it flips, so the deck goes on
   * tightening underneath the turn instead of snapping square in a single frame
   * at the exact moment the gate opens. Nothing here is unskippable:
   * `fastForward()`, a play, and the restack that follows one all finish both
   * jobs at once, and either job's `finalize` writes exactly the resting state
   * it would have reached on its own.
   *
   * `cards` is the stock in draw order, `cards[0]` being the card that will be
   * turned face up.
   */
  dealToStock(cards: readonly CardId[]): Promise<void> {
    const ids = cards.slice(0, DEAL_VISIBLE);
    const entries: { view: CardView; depth: number }[] = [];
    for (let depth = 0; depth < ids.length; depth++) {
      const view = this.#cards.get(ids[depth]!);
      if (view) entries.push({ view, depth });
    }
    if (entries.length === 0) return Promise.resolve();

    // Deepest first: the riffle cascades them down in this order, so the card
    // the player acts on is the last to land and ends on top.
    const order = [...entries].reverse();
    const last = order.length - 1;
    const rest = order.map((entry) => this.#stockSlot(entry.depth));
    const loose = order.map((entry) => this.#looseSlot(entry.depth));
    const reduced = prefersReducedMotion();

    const seat = (k: number): void => {
      const view = order[k]!.view;
      this.#applyStockSlot(view, rest[k]!, LIFT.rest);
      view.flip = 0;
      view.glow = 0;
    };

    const bodyViews = order.slice(0, last).map((entry) => entry.view);
    const body =
      bodyViews.length > 0
        ? this.#start(bodyViews, () => {
            for (let k = 0; k < last; k++) seat(k);
          })
        : null;
    const top = this.#start([order[last]!.view], () => seat(last));

    const ripple = dur(DUR.dealRipple);
    const sweep = dur(DUR.dealSweep);
    const rise = sweep * 0.45;
    // A deck slid onto felt barely leaves it — a low bow, not a throw.
    const arc = amp(SPAN.arcLow) * 0.4;
    const splitAt = ripple + sweep + dur(DUR.beat) * 0.25;
    const split = dur(DUR.riffleSplit);
    const step = dur(DUR.riffleStep);
    const snap = dur(DUR.riffleSnap);
    const spread = amp(SPAN.split);
    // The halves are fully parted before the first card is released; nothing
    // overlaps, so no two tweens ever fight for the same channel.
    const cascadeAt = splitAt + split;
    const settle = dur(DUR.settle) * 0.55;
    const square = dur(DUR.square);
    const touchdown = reduced ? ripple + sweep : cascadeAt + last * step + snap;

    order.forEach((entry, k) => {
      const view = entry.view;
      const home = rest[k]!;
      const untidy = loose[k]!;
      const tl = k < last && body ? body.tl : top.tl;
      const at = last > 0 ? (k / last) * ripple : 0;

      /* Beat 1 — the block comes on. Each card starts at its own offset within
       * the stack, translated off table, so the deck holds its shape in flight. */
      const origin = dealOrigin();
      const from = {
        x: origin.x + (untidy.x - STOCK.x),
        y: origin.y + (untidy.y - STOCK.y),
      };
      const landing = reduced ? home : untidy;

      tl.set(
        view,
        {
          visible: true,
          alpha: 1,
          flip: 0,
          glow: 0,
          lift: LIFT.hover,
          zIndex: home.z,
          x: from.x,
          y: from.y,
          rotation: landing.rotation - amp(TILT.entry),
        },
        at,
      );
      tl.set(view.scale, { x: 0.95, y: 0.95 }, at);
      tl.set(view.skew, { x: amp(TILT.skew) * 1.1, y: 0 }, at);

      tl.to(view, { x: landing.x, duration: sweep, ease: ease(EASE.throw) }, at);
      this.#arcY(tl, view, from.y, landing.y, at, sweep, rise, arc);
      tl.to(view, { rotation: landing.rotation, duration: sweep, ease: ease(EASE.travel) }, at);
      tl.to(view, { lift: LIFT.rest, duration: sweep, ease: EASE.fall }, at);
      tl.to(view.skew, { x: 0, duration: sweep, ease: ease(EASE.settle) }, at);
      tl.to(
        view.scale,
        { x: 1, y: 1, duration: sweep * 0.8, ease: ease(EASE.overshoot) },
        at + sweep * 0.2,
      );

      // Reduced motion stops here: the cards arrived, they are already square,
      // and there is no riffle to sit through.
      if (reduced) return;

      /* Beat 2a — the deck fans open and the halves part. Lift is doing the
       * work: each card rides higher than the one under it, so the shadows
       * spread into a fan and soften, and the two packets separate in depth as
       * well as across the table. */
      const side = riffleSide(entry.depth);
      const raise = lerp(LIFT.ready, LIFT.flight, last > 0 ? k / last : 1);
      // Overshoot rather than anticipate: the halves are pulled apart fast and
      // then *held*, which leaves a beat of stillness with the deck open and
      // the two shadow fans clear of each other. That held frame is what makes
      // the cascade that follows read as a riffle and not as a shuffle-shaped
      // pile of tweens.
      tl.to(
        view,
        { x: untidy.x + side * spread, duration: split, ease: ease(EASE.overshoot) },
        splitAt,
      );
      tl.to(view, { lift: raise, duration: split, ease: EASE.rise }, splitAt);
      tl.to(
        view,
        {
          rotation: untidy.rotation + side * amp(TILT.land) * 2.4,
          duration: split,
          ease: EASE.rise,
        },
        splitAt,
      );
      tl.to(view.skew, { x: amp(TILT.skew) * 0.9 * side, duration: split, ease: EASE.rise }, splitAt);
      tl.to(
        view.scale,
        { x: 1 + amp(POP.ready), y: 1 + amp(POP.ready), duration: split, ease: EASE.rise },
        splitAt,
      );

      /* Beat 2b — the interleave. One card every couple of frames, alternating
       * sides, each snapping onto the stack rather than easing onto it. The top
       * card is the exception: it drops straight onto its true slot, so the
       * gate opens with the card the player is about to flip already square and
       * the deck still tightening under it. */
      const dropAt = cascadeAt + k * step;
      const down = k < last ? untidy : home;
      tl.to(view, { x: down.x, duration: snap, ease: EASE.snap }, dropAt);
      tl.to(view, { y: down.y, duration: snap, ease: EASE.snap }, dropAt);
      tl.to(view, { rotation: down.rotation, duration: snap, ease: EASE.snap }, dropAt);
      tl.to(view, { lift: LIFT.rest, duration: snap, ease: EASE.fall }, dropAt);
      tl.to(view.skew, { x: 0, duration: snap * 0.8, ease: ease(EASE.settle) }, dropAt);
      tl.to(
        view.scale,
        {
          x: 1 + amp(POP.squash) * 0.6,
          y: 1 - amp(POP.squash) * 0.6,
          duration: snap * 0.5,
          ease: EASE.fall,
        },
        dropAt + snap * 0.5,
      );
      tl.to(view.scale, { x: 1, y: 1, duration: settle * 0.6, ease: ease(EASE.settle) }, dropAt + snap);

      /* Beat 3 — squaring. Sides first, then the ends: two taps, which is the
       * gesture itself rather than a decoration on top of it. Each tap presses
       * the deck into the felt, so the shadows tighten with the edges. */
      if (k < last) {
        tl.to(
          view,
          { x: home.x, rotation: home.rotation, duration: square, ease: ease(EASE.overshoot) },
          touchdown,
        );
        tl.to(view, { lift: LIFT.rest * 0.45, duration: square * 0.34, ease: EASE.fall }, touchdown);
        tl.to(
          view,
          { lift: LIFT.rest, duration: square * 0.66, ease: ease(EASE.settle) },
          touchdown + square * 0.34,
        );
        tl.to(
          view,
          { y: home.y, duration: square * 0.78, ease: ease(EASE.overshoot) },
          touchdown + square * 0.86,
        );
      }
    });

    // The tap the player feels rather than sees.
    if (body && !reduced) this.#impact(body.tl, touchdown, 0.13);

    if (body) this.#launch(body.job);
    this.#launch(top.job, touchdown);
    // Both halves run on one clock. `#launch` derives its time scale from how
    // many animations are live, and these two would each count the other as
    // pressure — which is fine when it agrees, and tears the deck in half when
    // it does not. Nothing competes with the opening, so both are pinned.
    body?.tl.timeScale(this.#speed);
    top.tl.timeScale(this.#speed);
    // And pinned against `#retune`, which re-times every live job whenever
    // pressure changes. Without this the two halves would be re-timed
    // independently the moment anything else started, which is exactly the
    // tearing this pin exists to prevent.
    if (body) body.job.pinned = true;
    top.job.pinned = true;

    return top.done;
  }

  /**
   * Turn the stock's top card face up. Anticipation as it lifts, a hard
   * edge-on beat where the face swaps, then a springy return. A wild gets a
   * flare on the swap and keeps a low glow while it is the active card,
   * because mistaking J♥ for J♠ is a lost bonus, not a cosmetic slip.
   */
  flipStockCard(card: CardId): Promise<void> {
    const view = this.#cards.get(card);
    if (!view) return Promise.resolve();

    const isWild = isWildCard(view.cardId);
    const restLift = isWild ? LIFT.hover : LIFT.rest;
    const restGlow = isWild ? WILD_IDLE_GLOW : 0;
    const restRot = amp(TILT.land) * 0.5 * jitter(7);
    const total = dur(DUR.flip);
    const half = total * 0.46;
    const back = total - half;
    const slot = this.#stockSlot(0);

    const finalize = (): void => {
      view.visible = true;
      view.alpha = 1;
      view.flip = 1;
      view.glow = restGlow;
      view.lift = restLift;
      view.x = slot.x;
      view.y = slot.y;
      view.rotation = restRot;
      view.scale.set(1, 1);
      view.skew.set(0, 0);
      view.zIndex = slot.z;
    };

    const { tl, done, job } = this.#start([view], finalize);

    tl.set(view, { visible: true, alpha: 1, glow: 0, zIndex: Z.hero }, 0);

    // Wind up and turn edge-on.
    tl.to(view, { lift: LIFT.hover, duration: half, ease: EASE.rise }, 0);
    // Two segments, so the face-swap lands exactly on the narrowest frame.
    //
    // `flip` crosses 0.5 — the threshold at which the card swaps from back to
    // face — halfway through its tween. As one linear tween over `total`, that
    // was 170ms, but the edge-on frame (scale.x -> 0.02) completes at
    // `half` = 156ms and then reopens on a `back.out(2.6)`. So the swap landed
    // after the card had already opened back out to roughly two-thirds width:
    // the player watched the BACK of the card widen, then blink to the face.
    // Splitting the tween at `half` pins the swap to the edge-on frame.
    tl.to(view, { flip: 0.5, duration: half, ease: EASE.none }, 0);
    tl.to(view, { flip: 1, duration: back, ease: EASE.none }, half);
    tl.to(view, { rotation: restRot, duration: total, ease: ease(EASE.settle) }, 0);
    tl.to(view.scale, { x: 0.02, duration: half, ease: ease(EASE.anticipate) }, 0);
    tl.to(view.scale, { y: 1 + amp(POP.ready), duration: half, ease: EASE.rise }, 0);
    tl.to(view.skew, { x: amp(TILT.skew), duration: half, ease: EASE.rise }, 0);

    // Open out again.
    tl.to(view.scale, { x: 1, duration: back, ease: ease(EASE.overshoot) }, half);
    tl.to(view.scale, { y: 1, duration: dur(DUR.settle) * 0.7, ease: ease(EASE.settle) }, half);
    tl.to(view.skew, { x: 0, duration: dur(DUR.settle) * 0.7, ease: ease(EASE.settle) }, half);
    tl.to(view, { lift: restLift, duration: dur(DUR.settle) * 0.8, ease: ease(EASE.settle) }, half);
    tl.set(view, { zIndex: slot.z }, half + back);

    if (isWild) {
      const flare = dur(DUR.flare);
      tl.to(view, { glow: 1, duration: flare * 0.28, ease: EASE.rise }, half);
      tl.to(view, { glow: restGlow, duration: flare * 0.72, ease: EASE.fall }, half + flare * 0.28);
    }

    this.#launch(job, Math.min(total * 0.78, dur(CRITICAL_PATH_MAX)));
    return done;
  }

  /* -------------------------------------------------- *
   * The core move
   * -------------------------------------------------- */

  /**
   * Fly the active card onto a pile. This runs hundreds of times a round, so
   * it has to be short, and it has to read.
   *
   * The shape: a beat of wind-up where the card lifts and counter-rotates
   * against the direction of travel; an arc across the table with the lift at
   * its peak — the shadow is a long way from the card here and that is the
   * whole illusion; then a squash on contact and an elastic settle that the
   * player is not made to wait for.
   *
   * Resolves the moment the card is down (~430ms), while the settle plays on.
   */
  /**
   * @param beneath - the cards already in the pile, bottom first, so the two
   *   directly under the landing can react to it. Optional: without it the play
   *   still works, it just lands on an inert stack.
   */
  playCardToPile(
    card: CardId,
    pileIndex: number,
    indexInPile: number,
    beneath?: readonly CardId[],
  ): Promise<void> {
    const view = this.#cards.get(card);
    if (!view) return Promise.resolve();

    const to = pileCardPosition(pileIndex, indexInPile);
    const restRot = amp(TILT.land) * jitter(pileIndex * 17 + indexInPile);
    const z = Z.pile + pileIndex * Z.pileStride + indexInPile;
    const dir = to.x >= view.x ? 1 : -1;
    const fromY = view.y;

    // The one or two cards directly under the landing, and where they rest.
    //
    // Resolved before `finalize` is built so that an interrupted play — which
    // kills the timeline mid-compression — still puts them back exactly. They
    // are deliberately NOT registered as job targets: they are settled cards,
    // and claiming ownership of them would let a landing cancel whatever they
    // were legitimately doing.
    const reactors: Array<{ view: CardView; id: CardId; y: number }> = [];
    if (beneath) {
      for (let k = 1; k <= 2; k++) {
        const index = indexInPile - k;
        if (index < 0 || index >= beneath.length) break;
        const id = beneath[index];
        if (id === undefined) break;
        const under = this.#cards.get(id);
        if (!under || under === view) continue;
        reactors.push({ view: under, id, y: under.y });
      }
    }

    const finalize = (): void => {
      view.visible = true;
      view.alpha = 1;
      view.flip = 1;
      view.glow = 0;
      view.lift = LIFT.rest;
      view.x = to.x;
      view.y = to.y;
      view.rotation = restRot;
      view.scale.set(1, 1);
      view.skew.set(0, 0);
      view.zIndex = z;
      for (const reactor of reactors) {
        // Borrowed, never owned — so unlike `view` above, this reference can go
        // stale underneath us. A landing that makes 21 clears the pile, and a
        // cleared card's sprite goes back to the pool and comes out as another
        // card. Restoring a remembered `y` onto it then strands whatever it is
        // now: `x` is never written here, so the card keeps the column it
        // belongs in and takes the row of a pile it was never in.
        if (reactor.view.cardId !== reactor.id) continue;
        reactor.view.lift = LIFT.rest;
        reactor.view.y = reactor.y;
      }
    };

    const { tl, done, job } = this.#start([view], finalize);

    // Flight time and arc height both scale with distance.
    //
    // The stock sits at x=250 and the four piles at 622/967/1312/1657, so the
    // horizontal spans differ by 3.8x. With a constant duration and a constant
    // arc that meant:
    //   - average speed varied 3.8x (1078 u/s to pile 0, 3911 u/s to pile 3),
    //     so the same throw gesture obeyed four different physics;
    //   - the bow collapsed from 29% of the chord at pile 0 (a lob) to 7.9% at
    //     pile 3 (near a straight line) — the longest, most common throw got
    //     the least arc;
    //   - peak velocity to pile 3 exceeded the card's motion-blur ceiling, so
    //     the smear covered only a fraction of the jump and it strobed.
    const reach = Math.hypot(to.x - view.x, to.y - view.y);
    const span = reach / 1450;

    const windUp = dur(DUR.anticipate);
    const flight = dur(DUR.travel * 0.86 * clamp(0.72 + 0.46 * span, 0.79, 1.37));
    const rise = flight * 0.44;
    const land = windUp + flight;
    const squash = dur(TICK * 0.8);
    const settle = dur(DUR.settle);
    // Held at roughly a sixth of the chord at every pile.
    const arc = amp(clamp(reach * 0.16, CARD.height * 0.2, CARD.height * 0.46));

    // `glow` is deliberately NOT in this set. A wild carries WILD_IDLE_GLOW on
    // the stock, and snapping it to 0 on frame 1 popped the highest-value card
    // in the game at the exact moment the player committed to it. It is carried
    // through the arc and faded out instead.
    tl.set(view, { visible: true, alpha: 1, flip: 1, glow: 0, zIndex: Z.flight }, 0);

    // Wind-up: lift off the stock and lean back against the throw.
    tl.to(view, { lift: LIFT.ready, duration: windUp, ease: ease(EASE.anticipate) }, 0);
    tl.to(
      view,
      { rotation: view.rotation - dir * amp(TILT.anticipate), duration: windUp, ease: EASE.rise },
      0,
    );
    tl.to(
      view.scale,
      { x: 1 + amp(POP.ready), y: 1 + amp(POP.ready), duration: windUp, ease: EASE.rise },
      0,
    );

    // Flight: arc across the table, lift peaking at the apex.
    tl.to(view, { x: to.x, duration: flight, ease: ease(EASE.throw) }, windUp);
    this.#arcY(tl, view, fromY, to.y, windUp, flight, rise, arc);

    tl.to(view, { lift: LIFT.flight, duration: rise, ease: EASE.rise }, windUp);
    tl.to(view, { lift: LIFT.rest, duration: flight - rise, ease: EASE.fall }, windUp + rise);

    tl.to(
      view,
      { rotation: restRot + dir * amp(TILT.flight), duration: rise, ease: EASE.rise },
      windUp,
    );
    // Follow-through starts AT contact, not in the air.
    //
    // These elastics used to begin at `windUp + rise` — 202ms before the card
    // touches — so by the moment of impact the curve was already 40% through
    // and the residual wobble was 0.006 rad (0.34 degrees). Follow-through is
    // the part of the body that keeps going after the mass stops; it cannot
    // happen before the mass arrives. The lean is now carried, partly relaxed,
    // through the descent and released on landing.
    tl.to(
      view,
      {
        rotation: restRot + dir * amp(TILT.flight) * 0.4,
        duration: flight - rise,
        ease: EASE.none,
      },
      windUp + rise,
    );
    tl.to(view, { rotation: restRot, duration: settle, ease: ease(EASE.settle) }, land);

    // The perspective warp — highlight and geometry pulling apart, then home.
    tl.to(view.skew, { x: amp(TILT.skew) * dir, duration: rise, ease: EASE.rise }, windUp);
    tl.to(view.skew, { x: 0, duration: dur(0.18), ease: ease(EASE.settle) }, land);

    tl.to(
      view.scale,
      { x: 1 + amp(POP.flight), y: 1 + amp(POP.flight), duration: rise, ease: EASE.rise },
      windUp,
    );
    // Cards squash more in Y than they widen in X — a sheet hitting a surface
    // loses height faster than it gains width.
    tl.to(
      view.scale,
      {
        x: 1 + amp(POP.squash) * 0.72,
        y: 1 - amp(POP.squash),
        duration: squash,
        ease: EASE.fall,
      },
      land - squash,
    );
    tl.to(view.scale, { x: 1, y: 1, duration: settle, ease: ease(EASE.settle) }, land);

    // Compress the card into the felt on contact and let it push back.
    //
    // `lift` drives the shadow's offset, blur and alpha, and the contact patch
    // *inversely* — so dipping it below rest is the one free impact punch the
    // shadow model offers, and nothing was using it. Previously lift arrived at
    // LIFT.rest on a power2.in and simply stopped, so the card was placed
    // rather than landed.
    const dip = dur(0.045);
    tl.to(view, { lift: LIFT.rest * 0.5, duration: dip, ease: EASE.fall }, land - dip);
    tl.to(view, { lift: LIFT.rest, duration: dur(0.2), ease: ease('back.out(2.2)') }, land);

    tl.set(view, { zIndex: z }, land);

    // Secondary motion: the stack answers the card that lands on it.
    //
    // Nothing in this file previously animated a card in reaction to another
    // card. The felt reacted (`table.impact`, `pulsePile`) but the objects did
    // not, and that inversion — ground responds, things don't — is what makes a
    // pile read as a decal rather than as a stack of paper.
    //
    // Expressed through `lift`, which the card layer already turns into shadow
    // offset, blur, alpha and an inverse contact patch. So a compression here
    // costs one tween and buys the whole visual consequence for free.
    reactors.forEach((reactor, i) => {
      // Nearer cards take more of it, and later, so the compression travels
      // down the stack rather than striking it all at once.
      const k = i + 1;
      const give = 0.28 / k;
      const at = land + k * 0.028;
      const drop = 1.6 / k;
      tl.to(
        reactor.view,
        { lift: LIFT.rest * (1 - give), y: reactor.y + drop, duration: 0.055, ease: EASE.fall },
        at,
      );
      tl.to(
        reactor.view,
        { lift: LIFT.rest, y: reactor.y, duration: 0.16, ease: ease('back.out(2)') },
        at + 0.055,
      );
    });

    this.#launch(job, Math.min(land, dur(CRITICAL_PATH_MAX)));
    return done;
  }

  /**
   * Undo: carry a card back off its pile to the stock. Lower arc and no
   * wind-up — an undo should feel like a rewind, not a second throw.
   */
  returnToStock(card: CardId): Promise<void> {
    const view = this.#cards.get(card);
    if (!view) return Promise.resolve();

    const slot = this.#stockSlot(0);
    const fromY = view.y;
    const dir = slot.x >= view.x ? 1 : -1;

    const finalize = (): void => {
      this.#applyStockSlot(view, slot, LIFT.rest);
      view.flip = 1;
      view.glow = isWildCard(view.cardId) ? WILD_IDLE_GLOW : 0;
    };

    const { tl, done, job } = this.#start([view], finalize);

    const fly = dur(DUR.travel * 0.78);
    const rise = fly * 0.4;

    tl.set(view, { visible: true, alpha: 1, flip: 1, zIndex: Z.flight }, 0);
    tl.to(view, { x: slot.x, duration: fly, ease: ease(EASE.travel) }, 0);
    this.#arcY(tl, view, fromY, slot.y, 0, fly, rise, amp(SPAN.arcLow));
    tl.to(view, { lift: LIFT.hover, duration: rise, ease: EASE.rise }, 0);
    tl.to(view, { lift: LIFT.rest, duration: fly - rise, ease: EASE.fall }, rise);
    tl.to(view, { rotation: slot.rotation, duration: fly, ease: ease(EASE.settle) }, 0);
    tl.to(view.skew, { x: amp(TILT.skew) * 0.6 * dir, duration: rise, ease: EASE.rise }, 0);
    tl.to(view.skew, { x: 0, duration: fly - rise, ease: ease(EASE.settle) }, rise);
    tl.set(view, { zIndex: slot.z }, fly);

    this.#launch(job, Math.min(fly, dur(CRITICAL_PATH_MAX)));
    return done;
  }

  /* -------------------------------------------------- *
   * Resolutions
   * -------------------------------------------------- */

  /**
   * A pile leaving the table. Four different shapes, because the player should
   * know which bonus fired from the movement alone, before the number lands:
   *
   * - `twentyOne` — snap together, lift as one, a beat of stillness, sweep off.
   *   Precision. The stillness is the point; it says "that was exact".
   * - `fiveCard`  — a riffle down the stack, each card kicking up in turn,
   *   then away. Accumulation.
   * - `wild`      — the wild flares and the rest are dragged into it before
   *   they all leave. Gravity, in the other sense.
   * - `bust`      — scatter, drop, tumble away with a sag. Heavier and slower
   *   than any of the wins, and the only one that shakes the table hard.
   *
   * Resolves within the critical-path budget; the sweep to the discard
   * finishes on its own so the player can keep playing through it.
   */
  resolvePile(pileIndex: number, kind: ResolveKind, cards: readonly CardId[]): Promise<void> {
    const ids: CardId[] = [];
    const views: CardView[] = [];
    for (const id of cards) {
      const view = this.#cards.get(id);
      if (!view) continue;
      ids.push(id);
      views.push(view);
    }
    if (views.length === 0) return Promise.resolve();

    const finalize = (): void => {
      for (const view of views) {
        view.visible = false;
        view.alpha = 1;
        view.glow = 0;
        view.lift = LIFT.rest;
        view.flip = 1;
        view.rotation = 0;
        view.x = DISCARD.x;
        view.y = DISCARD.y;
        view.scale.set(1, 1);
        view.skew.set(0, 0);
        view.zIndex = Z.pile;
      }
      // Nothing may still be animating a card at the moment it is handed back.
      //
      // This job's own tweens go with its timeline, but a landing compresses
      // the one or two cards beneath it *without owning them*, and those tweens
      // write `y` every frame. A pile that clears takes those cards with it, so
      // without this the compression outlives the card and goes on writing into
      // a sprite the pool has already reissued to somebody else.
      for (const view of views) gsap.killTweensOf(view);
      for (const id of ids) this.#cards.recycle?.(id);
    };

    const { tl, done, job } = this.#start(views, finalize);

    // Each builder returns the moment the outcome has been *read* — the beat
    // where the payoff is unmistakable and the pile starts to leave. That is
    // what the caller waits for; the sweep to the discard is a tail.
    let read: number;
    switch (kind) {
      case 'twentyOne':
        read = this.#buildTwentyOne(tl, views, pileIndex);
        break;
      case 'fiveCard':
        read = this.#buildFiveCard(tl, views);
        break;
      case 'wild':
        read = this.#buildWild(tl, views, pileIndex);
        break;
      case 'bust':
        read = this.#buildBust(tl, views, pileIndex);
        break;
    }

    this.#launch(job, Math.min(read, tl.duration(), dur(CRITICAL_PATH_MAX)));
    return done;
  }

  #buildTwentyOne(tl: GSAPTimeline, views: readonly CardView[], pileIndex: number): number {
    const base = pilePosition(pileIndex);
    const snap = dur(DUR.clear * 0.22);
    const raise = dur(DUR.clear * 0.26);
    const hold = dur(DUR.beat);
    const exit = dur(DUR.clear * 0.6);
    const raised = snap + raise;
    const leave = raised + hold;

    views.forEach((view, i) => {
      tl.set(view, { zIndex: Z.hero + i }, 0);
      tl.to(view, { x: base.x, y: base.y, rotation: 0, duration: snap, ease: EASE.snap }, 0);
      tl.to(view.skew, { x: 0, duration: snap, ease: EASE.snap }, 0);
      tl.to(view, { glow: 1, duration: snap, ease: EASE.rise }, 0);
      tl.to(view, { lift: LIFT.hero, duration: raise, ease: ease(EASE.overshoot) }, snap);
      tl.to(
        view.scale,
        { x: 1 + amp(POP.hero), y: 1 + amp(POP.hero), duration: raise, ease: ease(EASE.pop) },
        snap,
      );
      this.#sweepOff(tl, view, leave + i * dur(DUR.exitStagger), exit, i, false);
    });

    this.#impact(tl, snap, 0.26);
    return leave;
  }

  #buildFiveCard(tl: GSAPTimeline, views: readonly CardView[]): number {
    const step = dur(TICK * 0.7);
    const riffle = dur(DUR.clear * 0.28);
    const up = riffle * 0.45;
    const down = riffle - up;
    const exit = dur(DUR.clear * 0.5);
    const leave = (views.length - 1) * step + riffle + dur(DUR.beat) * 0.6;

    views.forEach((view, i) => {
      const at = i * step;
      const side = i % 2 === 0 ? 1 : -1;
      tl.set(view, { zIndex: Z.hero + i }, 0);

      tl.to(view, { x: `+=${amp(SPAN.riffle) * side}`, duration: up, ease: EASE.rise }, at);
      tl.to(view, { rotation: `+=${amp(TILT.flight) * side}`, duration: up, ease: EASE.rise }, at);
      tl.to(view, { lift: LIFT.flight, duration: up, ease: EASE.rise }, at);
      tl.to(view, { glow: 1, duration: up, ease: EASE.rise }, at);
      tl.to(
        view.scale,
        { x: 1 + amp(POP.flight), y: 1 + amp(POP.flight), duration: up, ease: EASE.rise },
        at,
      );

      tl.to(view, { lift: LIFT.hero * 0.7, duration: down, ease: ease(EASE.settle) }, at + up);
      tl.to(view.scale, { x: 1, y: 1, duration: down, ease: ease(EASE.settle) }, at + up);

      this.#sweepOff(tl, view, leave + i * dur(DUR.exitStagger), exit, i, false);
    });

    this.#impact(tl, leave, 0.2);
    return leave;
  }

  #buildWild(tl: GSAPTimeline, views: readonly CardView[], pileIndex: number): number {
    const base = pilePosition(pileIndex);
    const heroIndex = Math.max(
      0,
      views.findIndex((view) => isWildCard(view.cardId)),
    );
    const hero = views[heroIndex]!;
    const others = views.filter((view) => view !== hero);

    const flare = dur(DUR.flare);
    const draw = dur(DUR.clear * 0.42);
    const hold = dur(DUR.beat) * 0.8;
    const exit = dur(DUR.clear * 0.55);
    const drawAt = flare * 0.55;
    const stagger = dur(DUR.exitStagger);
    const absorbed = drawAt + draw + Math.max(0, others.length - 1) * stagger;
    const pulse = dur(TICK);
    const leave = absorbed + pulse * 2.6 + hold;

    tl.set(hero, { zIndex: Z.hero + 40 }, 0);
    tl.to(hero, { x: base.x, y: base.y, rotation: 0, duration: flare * 0.5, ease: EASE.snap }, 0);
    tl.to(hero.skew, { x: 0, duration: flare * 0.5, ease: EASE.snap }, 0);
    tl.to(hero, { glow: 1, duration: flare * 0.35, ease: EASE.rise }, 0);
    tl.to(hero, { lift: LIFT.hero, duration: flare * 0.6, ease: ease(EASE.overshoot) }, 0);
    tl.to(
      hero.scale,
      { x: 1 + amp(POP.hero), y: 1 + amp(POP.hero), duration: flare * 0.6, ease: ease(EASE.pop) },
      0,
    );

    others.forEach((view, i) => {
      const at = drawAt + i * stagger;
      tl.set(view, { zIndex: Z.hero + i }, 0);
      tl.to(
        view,
        { x: base.x, y: base.y, rotation: 0, duration: draw, ease: ease(EASE.anticipate) },
        at,
      );
      tl.to(view.skew, { x: 0, duration: draw, ease: EASE.rise }, at);
      tl.to(view, { lift: LIFT.hero * 0.8, duration: draw, ease: EASE.rise }, at);
      tl.to(view, { glow: 0.6, duration: draw, ease: EASE.rise }, at);
      tl.to(
        view.scale,
        {
          x: 1 + amp(POP.absorb),
          y: 1 + amp(POP.absorb),
          duration: draw,
          ease: ease(EASE.anticipate),
        },
        at,
      );
    });

    // The wild swallows them and flares again.
    const peak = 1 + amp(POP.hero) * 1.7;
    tl.to(hero.scale, { x: peak, y: peak, duration: pulse, ease: ease(EASE.pop) }, absorbed);
    tl.to(
      hero.scale,
      {
        x: 1 + amp(POP.hero),
        y: 1 + amp(POP.hero),
        duration: pulse * 1.6,
        ease: ease(EASE.settle),
      },
      absorbed + pulse,
    );

    views.forEach((view, i) => {
      this.#sweepOff(tl, view, leave + i * stagger, exit, i, false);
    });

    this.#impact(tl, absorbed, 0.34);
    return absorbed + pulse;
  }

  #buildBust(tl: GSAPTimeline, views: readonly CardView[], pileIndex: number): number {
    const kick = dur(DUR.bust * 0.16);
    const drop = dur(DUR.bust * 0.34);
    const rebound = drop * 0.5;
    const pause = dur(DUR.beat) * 0.7;
    const exit = dur(DUR.bust * 0.62);
    const leave = kick + drop + rebound + pause;
    const n = views.length;

    views.forEach((view, i) => {
      const spread = i - (n - 1) / 2;
      const angle = -Math.PI / 2 + spread * 0.55;
      const radius = amp(SPAN.scatter) * (0.55 + 0.45 * Math.abs(jitter(i * 13 + pileIndex)));
      const dx = Math.cos(angle) * radius;
      const dy = Math.sin(angle) * radius * 0.55;
      const spin = spread >= 0 ? 1 : -1;

      tl.set(view, { zIndex: Z.hero + i, glow: 0 }, 0);

      // Kick — the pile comes apart.
      tl.to(view, { x: `+=${dx}`, y: `+=${dy}`, duration: kick, ease: ease(EASE.pop) }, 0);
      tl.to(view, { lift: LIFT.flight * 0.8, duration: kick, ease: EASE.rise }, 0);
      tl.to(
        view,
        { rotation: `+=${amp(TILT.exit) * spin}`, duration: kick + drop, ease: EASE.none },
        0,
      );
      tl.to(
        view.scale,
        {
          x: 1 + amp(POP.flight) * 0.6,
          y: 1 + amp(POP.flight) * 0.6,
          duration: kick,
          ease: ease(EASE.pop),
        },
        0,
      );

      // Drop — dead weight, all the way to the felt.
      tl.to(view, { lift: LIFT.flat, duration: drop, ease: ease(EASE.heavy) }, kick);
      tl.to(view, { y: `+=${amp(SPAN.sag) * 0.5}`, duration: drop, ease: ease(EASE.heavy) }, kick);
      tl.to(
        view.scale,
        {
          x: 1 + amp(POP.squash) * 1.4,
          y: 1 - amp(POP.squash) * 1.4,
          duration: drop * 0.3,
          ease: EASE.fall,
        },
        kick + drop * 0.7,
      );
      tl.to(view.scale, { x: 1, y: 1, duration: rebound, ease: ease(EASE.overshoot) }, kick + drop);

      // Tumble away, sagging as it goes.
      this.#sweepOff(tl, view, leave + i * dur(DUR.exitStagger), exit, i, true);
    });

    this.#impact(tl, kick + drop * 0.9, 0.68);
    return leave;
  }

  /* -------------------------------------------------- *
   * Feedback
   * -------------------------------------------------- */

  /**
   * Pile feedback. `hover`/`hoverOut` are states, `press` and `reject` are
   * pulses. Pass the pile's top card and it gets the same treatment as the
   * well, which is what makes the pile feel like a physical stack rather than
   * a hit region.
   */
  nudgePile(pileIndex: number, kind: NudgeKind = 'press', topCard?: CardId): Promise<void> {
    const well = this.#wells[pileIndex] ?? null;
    const card = topCard !== undefined ? (this.#cards.get(topCard) ?? null) : null;
    if (!well && !card) return Promise.resolve();

    if (kind === 'hover') this.#hovered = pileIndex;
    else if (kind === 'hoverOut' && this.#hovered === pileIndex) this.#hovered = null;

    const hovering = this.#hovered === pileIndex;
    const targets: object[] = [];
    if (well) targets.push(well);
    if (card) targets.push(card);

    const wellBase = well ? well.x : 0;
    const cardBase = card ? card.x : 0;
    // Every kind returns to whatever the hover state currently is, so a press
    // or a rejection in the middle of a hover does not drop the hover.
    const restScale = hovering ? 1 + amp(POP.hover) : 1;
    const restLift = hovering ? LIFT.hover : LIFT.rest;

    const finalize = (): void => {
      if (well) {
        well.x = wellBase;
        well.scale.set(restScale, restScale);
      }
      if (card) {
        card.x = cardBase;
        card.lift = restLift;
        card.scale.set(1, 1);
      }
    };

    const { tl, done, job } = this.#start(targets, finalize);

    if (kind === 'reject') {
      const span = dur(DUR.reject);
      const swing = amp(SPAN.reject);
      if (swing > 0) {
        // Linear phase, squared amplitude — same correction as `shake()`; see
        // the long note there for why deriving time from an eased proxy makes
        // the oscillation slow to a stop instead of decaying.
        //
        // The well and the card also no longer move in lockstep: identical
        // offsets on two objects at different depths read as a texture sliding
        // rather than as a physical rebuff. The card leads with more amplitude.
        const proxy = { p: 1 };
        tl.to(
          proxy,
          {
            p: 0,
            duration: span,
            ease: EASE.none,
            onUpdate: () => {
              const t = (1 - proxy.p) * span;
              const decay = proxy.p * proxy.p;
              const phase = t * SHAKE_FREQ * 1.4;
              if (well) well.x = wellBase + Math.sin(phase) * swing * decay;
              if (card) card.x = cardBase + Math.sin(phase - 0.42) * swing * 1.35 * decay;
            },
          },
          0,
        );
      }
    } else if (kind === 'press') {
      const down = dur(DUR.press);
      const upTime = dur(DUR.hover);
      const pressed = 1 + amp(POP.press);
      if (well) {
        tl.to(well.scale, { x: pressed, y: pressed, duration: down, ease: EASE.fall }, 0);
        tl.to(
          well.scale,
          { x: restScale, y: restScale, duration: upTime, ease: ease(EASE.pop) },
          down,
        );
      }
      if (card) {
        tl.to(card, { lift: LIFT.rest, duration: down, ease: EASE.fall }, 0);
        tl.to(card, { lift: restLift, duration: upTime, ease: ease(EASE.pop) }, down);
      }
    } else {
      const span = dur(DUR.hover);
      if (well) {
        tl.to(
          well.scale,
          { x: restScale, y: restScale, duration: span, ease: ease(EASE.overshoot) },
          0,
        );
      }
      if (card) tl.to(card, { lift: restLift, duration: span, ease: ease(EASE.overshoot) }, 0);
    }

    this.#launch(job);
    return done;
  }

  /**
   * Screen shake. `intensity` 0..1.
   *
   * Amplitude scales with intensity; the oscillation *rate* does not. A big
   * shake is a wider shake, never a slower one — slow shake stops reading as
   * impact and starts reading as a dropped frame. Everything decays to zero
   * and the target is restored exactly, so this is safe to interrupt.
   *
   * Dropped entirely under reduced motion.
   */
  shake(intensity = 0.5): Promise<void> {
    const target = this.#shakeTarget;
    if (!target) return Promise.resolve();

    const i = clamp01(intensity);
    const swing = amp(lerp(SPAN.shakeMin, SPAN.shakeMax, i));
    if (swing <= 0) return Promise.resolve();

    const span = dur(lerp(DUR.shakeMin, DUR.shakeMax, i));
    const roll = amp(TILT.shake) * i;
    const phase = (this.#shakeSeed++ % 8) * 0.7;

    // Capture the untouched transform once; an interrupting shake reuses it.
    const previous = this.#owner.get(target);
    if (previous) this.#finish(previous);
    const base = this.#shakeBase ?? { x: target.x, y: target.y, rotation: target.rotation };
    this.#shakeBase = base;

    const finalize = (): void => {
      target.x = base.x;
      target.y = base.y;
      target.rotation = base.rotation;
      this.#shakeBase = null;
    };

    const { tl, done, job } = this.#start([target], finalize);

    // Phase advances in REAL time; only the amplitude decays.
    //
    // This previously drove the proxy with `EASE.calm` (power2.out) and then
    // derived `t` from the proxy, which made `dt/du = 2(1-u)*span` — the
    // oscillation ran at twice `SHAKE_FREQ` at the start and reached exactly
    // ZERO frequency at the end. A bust spent half its total phase in the first
    // 29% of the shake and then spent a quarter of a second drifting back into
    // place. The docstring above this method says a big shake is "a wider
    // shake, never a slower one — slow shake stops reading as impact and starts
    // reading as a dropped frame", and the implementation did precisely the
    // thing it forbids.
    //
    // A linear proxy keeps the pitch constant; squaring it for the amplitude
    // keeps the fast falloff that made the old curve feel punchy.
    const proxy = { p: 1 };
    tl.to(
      proxy,
      {
        p: 0,
        duration: span,
        ease: EASE.none,
        onUpdate: () => {
          const t = (1 - proxy.p) * span;
          const decay = proxy.p * proxy.p;
          target.x = base.x + Math.sin(t * SHAKE_FREQ + phase) * swing * decay;
          target.y = base.y + Math.sin(t * SHAKE_FREQ * 0.77 + phase * 1.7) * swing * 0.72 * decay;
          target.rotation = base.rotation + Math.sin(t * SHAKE_FREQ * 0.61) * roll * decay;
        },
      },
      0,
    );

    this.#launch(job);
    return done;
  }

  /* -------------------------------------------------- *
   * Instant placement — undo, restore, resize
   * -------------------------------------------------- */

  /** Put a card in a pile with no animation. Used when rebuilding state. */
  placeInPile(card: CardId, pileIndex: number, indexInPile: number): void {
    const view = this.#cards.get(card);
    if (!view) return;
    this.#interrupt(view);
    const to = pileCardPosition(pileIndex, indexInPile);
    view.visible = true;
    view.alpha = 1;
    view.flip = 1;
    view.glow = 0;
    view.lift = LIFT.rest;
    view.x = to.x;
    view.y = to.y;
    view.rotation = amp(TILT.land) * jitter(pileIndex * 17 + indexInPile);
    view.scale.set(1, 1);
    view.skew.set(0, 0);
    view.zIndex = Z.pile + pileIndex * Z.pileStride + indexInPile;
  }

  /**
   * Put a card on the stock with no animation. `depth` 0 is the top card;
   * `faceUp` should be true only for the active card.
   */
  placeInStock(card: CardId, depth = 0, faceUp = false): void {
    const view = this.#cards.get(card);
    if (!view) return;
    this.#interrupt(view);
    const slot = this.#stockSlot(depth);
    this.#applyStockSlot(view, slot, LIFT.rest);
    view.flip = faceUp ? 1 : 0;
    view.glow = faceUp && isWildCard(view.cardId) ? WILD_IDLE_GLOW : 0;
    if (faceUp) view.lift = isWildCard(view.cardId) ? LIFT.hover : LIFT.rest;
  }

  /** Take cards off the table immediately and hand them back to the pool. */
  hide(cards: readonly CardId[]): void {
    for (const id of cards) {
      const view = this.#cards.get(id);
      if (!view) continue;
      this.#interrupt(view);
      view.visible = false;
      view.alpha = 1;
      view.glow = 0;
      view.lift = LIFT.rest;
      view.scale.set(1, 1);
      view.skew.set(0, 0);
      // Same reason as the sweep: a borrowed compression tween is not this
      // view's job to kill, and it must not follow the sprite into the pool.
      gsap.killTweensOf(view);
      this.#cards.recycle?.(id);
    }
  }

  /* -------------------------------------------------- *
   * Internals
   * -------------------------------------------------- */

  #views(ids: readonly CardId[]): CardView[] {
    const out: CardView[] = [];
    for (const id of ids) {
      const view = this.#cards.get(id);
      if (view) out.push(view);
    }
    return out;
  }

  #stockSlot(depth: number): { x: number; y: number; rotation: number; z: number } {
    return {
      x: STOCK.x + depth * STOCK_STEP * 0.55,
      y: STOCK.y + depth * STOCK_STEP,
      rotation: amp(TILT.land) * 0.5 * jitter(depth * 31 + 7),
      z: Z.stock + (DEAL_VISIBLE - depth),
    };
  }

  /**
   * Where a card sits before the deck is squared: its own slot, knocked out of
   * true. Deterministic like every other jitter here, so a restart deals the
   * same untidy deck and the squaring always tightens the same distance.
   *
   * Under reduced motion `amp` zeroes the scatter, which collapses this onto
   * `#stockSlot` exactly — so the deck arrives already square and the squaring
   * beat costs nothing rather than having to be branched around.
   */
  #looseSlot(depth: number): { x: number; y: number; rotation: number; z: number } {
    const slot = this.#stockSlot(depth);
    const scatter = amp(SPAN.loose);
    return {
      x: slot.x + jitter(depth * 41 + 13) * scatter,
      y: slot.y + jitter(depth * 67 + 29) * scatter * 0.7,
      rotation: slot.rotation + jitter(depth * 23 + 5) * amp(TILT.loose),
      z: slot.z,
    };
  }

  #applyStockSlot(
    view: CardView,
    slot: { x: number; y: number; rotation: number; z: number },
    lift: number,
  ): void {
    view.visible = true;
    view.alpha = 1;
    view.lift = lift;
    view.x = slot.x;
    view.y = slot.y;
    view.rotation = slot.rotation;
    view.scale.set(1, 1);
    view.skew.set(0, 0);
    view.zIndex = slot.z;
  }

  /**
   * The arc. Two y tweens sharing one x tween: out fast and up, then down
   * under something that behaves like gravity. Straightens automatically when
   * `arc` is zero, which is what reduced motion asks for.
   */
  #arcY(
    tl: GSAPTimeline,
    view: CardView,
    fromY: number,
    toY: number,
    at: number,
    length: number,
    rise: number,
    arc: number,
  ): void {
    if (arc <= 0) {
      tl.to(view, { y: toY, duration: length, ease: ease(EASE.travel) }, at);
      return;
    }
    const apex = Math.min(fromY, toY) - arc;
    tl.to(view, { y: apex, duration: rise, ease: EASE.rise }, at);
    tl.to(view, { y: toY, duration: length - rise, ease: EASE.fall }, at + rise);
  }

  /** The common exit: off toward the discard, shrinking, spinning, fading. */
  #sweepOff(
    tl: GSAPTimeline,
    view: CardView,
    at: number,
    length: number,
    index: number,
    heavy: boolean,
  ): void {
    const spin = amp(heavy ? TILT.tumble : TILT.exit) * (jitter(index * 7 + 3) >= 0 ? 1 : -1);
    const sag = heavy ? amp(SPAN.sag) : 0;

    tl.to(view, { x: DISCARD.x, duration: length, ease: ease(EASE.exit) }, at);
    if (sag > 0) {
      tl.to(view, { y: `+=${sag}`, duration: length * 0.42, ease: EASE.fall }, at);
      tl.to(
        view,
        { y: DISCARD.y, duration: length * 0.58, ease: ease(EASE.exit) },
        at + length * 0.42,
      );
    } else {
      tl.to(view, { y: DISCARD.y, duration: length, ease: ease(EASE.exit) }, at);
    }
    tl.to(view, { rotation: `+=${spin}`, duration: length, ease: EASE.none }, at);
    tl.to(view, { glow: 0, duration: length * 0.5, ease: EASE.fall }, at);
    tl.to(
      view.scale,
      { x: 1 + amp(POP.exit), y: 1 + amp(POP.exit), duration: length, ease: ease(EASE.exit) },
      at,
    );

    // A win leaves and a bust is dragged away. They should not exit alike.
    //
    // Both used to drop to LIFT.flat over the whole exit — so cards that had
    // just been held at LIFT.hero fell to zero shadow separation *while
    // accelerating away*. For a bust that is right: dead weight sliding off the
    // table. For a win it is backwards; a triumphant exit should rise.
    //
    // The fade timing was also wrong for both. `EASE.exit` is power2.in, so at
    // the old fade start (55% of the way through) the card had covered only 30%
    // of its run — for a pile-0 card that put it dead centre of the screen, and
    // the win visibly evaporated over the middle of the table instead of
    // leaving it. Holding the fade to the last sliver means the card is off
    // frame under its own motion and the alpha is only insurance.
    if (heavy) {
      tl.to(view, { lift: LIFT.flat, duration: length, ease: EASE.fall }, at);
      tl.to(view, { alpha: 0, duration: length * 0.45, ease: EASE.fall }, at + length * 0.55);
    } else {
      tl.to(view, { lift: LIFT.hero * 1.25, duration: length * 0.6, ease: EASE.rise }, at);
      tl.to(view, { alpha: 0, duration: length * 0.14, ease: EASE.fall }, at + length * 0.86);
    }
  }

  /**
   * Schedule a shake from inside a resolution timeline. Fires as a callback,
   * which means a fast-forward that kills the timeline also cancels the shake
   * — exactly right: nobody wants five queued shakes after a fast round.
   */
  #impact(tl: GSAPTimeline, at: number, intensity: number): void {
    if (!this.#autoShake) return;
    tl.call(
      () => {
        void this.shake(intensity);
      },
      undefined,
      at,
    );
  }

  #start(
    targets: readonly object[],
    finalize: () => void,
  ): { tl: GSAPTimeline; done: Promise<void>; job: Job } {
    for (const target of targets) this.#interrupt(target);
    this.#trim();

    let resolveFn: () => void = () => {};
    const done = new Promise<void>((resolve) => {
      resolveFn = resolve;
    });

    const tl = gsap.timeline({ paused: true });
    const job: Job = {
      tl,
      targets: [...targets],
      finalize,
      resolve: resolveFn,
      resolved: false,
      finished: false,
      pinned: false,
    };

    tl.eventCallback('onComplete', () => this.#finish(job));
    this.#live.add(job);
    // Pressure has just changed; everything already running must be re-timed.
    this.#retune();
    for (const target of targets) this.#owner.set(target, job);
    return { tl, done, job };
  }

  /**
   * Start the timeline. `resolveAt` is the timeline time at which the caller's
   * promise resolves — the moment the move is legible, which is usually well
   * before the decorative tail finishes.
   */
  #launch(job: Job, resolveAt?: number): void {
    if (resolveAt !== undefined && resolveAt > 0 && resolveAt < job.tl.duration()) {
      job.tl.call(() => this.#settle(job), undefined, resolveAt);
    }
    job.tl.timeScale(this.#timeScale());
    job.tl.play(0);
  }

  /** Concurrency turns into speed, never into a queue. */
  #timeScale(): number {
    const pressure = Math.max(0, this.#live.size - 1);
    return (
      this.#speed *
      Math.min(1 + pressure * PRESSURE_GAIN, MAX_TIME_SCALE) *
      this.#hitStopFactor
    );
  }

  /**
   * Re-apply the current time scale to every live job.
   *
   * `#timeScale()` reads `#live.size`, but it was only ever sampled once, in
   * `#launch`. So a card thrown alone kept playing at 1.0 even as three more
   * stacked up behind it — the backlog the pressure model exists to prevent —
   * and a card thrown under pressure stayed fast long after the table emptied,
   * visibly outrunning the card played after it. Two identical plays ran at
   * different speeds and a later card could overtake an earlier one.
   *
   * Ramped rather than stepped so the compression is continuous. Jobs that
   * opted out (the opening deal, which is deliberately pinned) keep their own
   * scale.
   */
  #retune(): void {
    const target = this.#timeScale();
    for (const job of this.#live) {
      if (job.pinned) continue;
      gsap.to(job.tl, {
        timeScale: target,
        duration: 0.08,
        ease: 'power1.out',
        overwrite: 'auto',
      });
    }
    this.#onTimeScale?.(this.#hitStopFactor);
  }

  /**
   * Freeze the table briefly. Used on impacts that should land with weight.
   *
   * @param seconds - real-time duration of the stop.
   * @param factor - time scale during it. 0.08 is a near-freeze; 0.35 is a drag.
   */
  hitStop(seconds: number, factor: number): void {
    // Time dilation is motion. Under reduced motion it is simply off.
    if (prefersReducedMotion()) return;
    const next = clamp(factor, 0.02, 1);
    // A stronger stop wins; a weaker one must not shorten a freeze in flight.
    if (next < this.#hitStopFactor || this.#hitStopTimer === null) {
      this.#hitStopFactor = next;
      this.#retune();
    }
    if (this.#hitStopTimer !== null) clearTimeout(this.#hitStopTimer);
    this.#hitStopTimer = setTimeout(
      () => {
        this.#hitStopTimer = null;
        this.#hitStopFactor = 1;
        this.#retune();
      },
      Math.max(seconds, 0) * 1000,
    );
  }

  /**
   * Notified whenever the hit-stop factor changes.
   *
   * Anything animating off `gsap.ticker` rather than off a timeline — the felt
   * ripple and the lamp clock in `@/pixi/table` — is NOT affected by a timeline
   * time scale, so it would keep running at full rate through a freeze and tear
   * away from the cards. Those have to be told.
   */
  set onTimeScale(fn: ((factor: number) => void) | undefined) {
    this.#onTimeScale = fn;
  }

  #trim(): void {
    while (this.#live.size >= MAX_CONCURRENT) {
      let oldest: Job | undefined;
      for (const job of this.#live) {
        oldest = job;
        break;
      }
      if (!oldest) break;
      this.#finish(oldest);
    }
  }

  #interrupt(target: object): void {
    const job = this.#owner.get(target);
    if (job) this.#finish(job);
  }

  #settle(job: Job): void {
    if (job.resolved) return;
    job.resolved = true;
    job.resolve();
  }

  #finish(job: Job): void {
    if (job.finished) return;
    job.finished = true;
    job.tl.kill();
    this.#live.delete(job);
    // Pressure has dropped; let whatever is still running relax back down.
    this.#retune();
    for (const target of job.targets) {
      if (this.#owner.get(target) === job) this.#owner.delete(target);
    }
    job.finalize();
    this.#settle(job);
  }

  #restoreShake(): void {
    const target = this.#shakeTarget;
    const base = this.#shakeBase;
    if (!target || !base) return;
    target.x = base.x;
    target.y = base.y;
    target.rotation = base.rotation;
    this.#shakeBase = null;
  }
}
